import {
  Alert,
  Card,
  Spinner,
  Table,
  formatCurrency,
  formatNumber,
  type TableColumn,
} from '@/ui'
import type { ChannelFunnelResult, ChannelFunnelRow } from './types'
import sharedStyles from '../PaymentAttribution.module.css'
import styles from './ChannelFunnelTab.module.css'

function formatNullableNumber(value: number | null): string {
  return value == null ? '-' : formatNumber(value)
}

function formatNullableCurrency(value: number | null): string {
  return value == null ? '-' : formatCurrency(value)
}

function formatRate(value: number | null): string {
  return value == null ? '-' : `${(value * 100).toFixed(1)}%`
}

function formatRoas(value: number | null): string {
  return value == null ? '-' : `${(value * 100).toFixed(0)}%`
}

const columns: TableColumn<ChannelFunnelRow>[] = [
  { key: 'channel', label: '채널', render: (r) => r.label || r.channel },
  { key: 'sessions', label: '세션(GA4)', align: 'right', render: (r) => formatNullableNumber(r.sessions) },
  { key: 'signups', label: '가입', align: 'right', render: (r) => formatNumber(r.signups) },
  { key: 'payers', label: '결제자', align: 'right', render: (r) => formatNumber(r.payers) },
  { key: 'paymentsCount', label: '결제건', align: 'right', render: (r) => formatNumber(r.paymentsCount) },
  { key: 'netRevenue', label: '순매출', align: 'right', render: (r) => formatCurrency(r.netRevenue) },
  { key: 'adSpend', label: '광고비', align: 'right', render: (r) => formatNullableCurrency(r.adSpend) },
  { key: 'roas', label: 'ROAS', align: 'right', render: (r) => formatRoas(r.roas) },
  { key: 'cvrSignupToPay', label: '가입→결제%', align: 'right', render: (r) => formatRate(r.cvrSignupToPay) },
]

function hasUnmapped(data: ChannelFunnelResult): boolean {
  return data.unmapped.ga4.length > 0 || data.unmapped.utm.length > 0
}

interface ChannelFunnelTabProps {
  data: ChannelFunnelResult | null
  error: string | null
  isLoading: boolean
}

export function ChannelFunnelTab({ data, error, isLoading }: ChannelFunnelTabProps) {
  if (error) return <Alert variant="danger">불러오지 못했습니다: {error}</Alert>
  if (isLoading && !data) return <div className={sharedStyles.center}><Spinner /></div>

  return (
    <div className={styles.root}>
      {data && data.ga4Available === false && (
        <Alert variant="warning" title="GA4 미연동">
          GA4 세션 데이터를 불러오지 못해 세션 컬럼은 '-'로 표시하고, DB 가입·결제 지표만 표시합니다.
        </Alert>
      )}

      <Card>
        <p className={sharedStyles.cardTitle}>채널별 퍼널</p>
        <Table<ChannelFunnelRow>
          columns={columns}
          data={data?.channels ?? []}
          rowKey={(r) => r.channel}
          size="sm"
          striped
          isLoading={isLoading && !data}
          emptyMessage="해당 기간 퍼널 데이터가 없습니다."
        />
        <p className={sharedStyles.cardCaption}>GA4 세션과 DB 가입·결제는 측정 체계가 달라 전환율은 참고치입니다.</p>
      </Card>

      {data && hasUnmapped(data) && (
        <details className={styles.unmappedDetails}>
          <summary className={styles.summary}>매핑되지 않은 원본 소스 보기</summary>
          <div className={styles.sourceGrid}>
            <div className={styles.sourceList}>
              <p className={styles.sourceTitle}>GA4 source / medium</p>
              {data.unmapped.ga4.length > 0 ? (
                <ul className={styles.rawList}>
                  {data.unmapped.ga4.map((row) => (
                    <li key={row.sourceMedium}>
                      <span>{row.sourceMedium}</span>
                      <span>{formatNumber(row.sessions)} 세션</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.emptyText}>없음</p>
              )}
            </div>
            <div className={styles.sourceList}>
              <p className={styles.sourceTitle}>UTM source / medium</p>
              {data.unmapped.utm.length > 0 ? (
                <ul className={styles.rawList}>
                  {data.unmapped.utm.map((row, index) => (
                    <li key={`${row.source ?? 'null'}-${row.medium ?? 'null'}-${index}`}>
                      <span>{row.source ?? '(none)'} / {row.medium ?? '(none)'}</span>
                      <span>{formatNumber(row.signups)} 가입</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.emptyText}>없음</p>
              )}
            </div>
          </div>
        </details>
      )}
    </div>
  )
}
